import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createImageRecovery } from './imageRecovery';

export function isRecoverableImage(src) {
  if (typeof src !== 'string' || !src.trim()) return false;
  // Build assets and local previews are not remote requests to retry.
  if (/^\/?static\//.test(src)) return false;
  try {
    const url = new URL(src, window.location.href);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}
function ImageSession({
  src,
  alt = '',
  fallback,
  fallbackSrc,
  style,
  onLoad,
  onError,
  onFailureChange,
  ...props
}) {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  const ref = useRef(null);
  const recoverable = isRecoverableImage(src);
  const callbacks = useRef({ onLoad, onError, onFailureChange });
  callbacks.current = { onLoad, onError, onFailureChange };
  // Include stalled requests, and reset the parent's fallback geometry on each new URL.
  useLayoutEffect(() => {
    callbacks.current.onFailureChange?.(failed);
  }, [failed]);
  const recovery = useMemo(() => createImageRecovery(setAttempt, () => setFailed(true)), []);
  useLayoutEffect(() => {
    if (!recoverable) return;
    const element = ref.current;
    let live = true;
    let near = typeof IntersectionObserver === 'undefined';
    const active = () =>
      near && document.visibilityState !== 'hidden' && navigator.onLine !== false;
    const update = () => {
      if (live) recovery.setActive(active());
    };
    recovery.start(active());
    const observer =
      typeof IntersectionObserver !== 'undefined'
        ? new IntersectionObserver(
            ([entry]) => {
              near = entry.isIntersecting;
              update();
            },
            { rootMargin: '200px' },
          )
        : null;
    // Observe the stable container: the img is remounted on retry and may be hidden by fallback.
    observer?.observe(element.parentElement);
    document.addEventListener('visibilitychange', update);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      live = false;
      recovery.dispose();
      observer?.disconnect();
      document.removeEventListener('visibilitychange', update);
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, [recoverable, recovery]);
  const hasFallback = Boolean(fallback || fallbackSrc);
  return (
    <>
      <img
        {...props}
        key={attempt}
        ref={ref}
        src={src}
        alt={alt}
        loading={attempt > 0 ? 'eager' : props.loading}
        style={
          failed
            ? { ...style, ...(hasFallback ? { display: 'none' } : { visibility: 'hidden' }) }
            : style
        }
        onLoad={(event) => {
          if (!recoverable || recovery.loaded(attempt)) {
            setFailed(false);
            callbacks.current.onLoad?.(event);
          }
        }}
        onError={(event) => {
          if (!recoverable || recovery.failed(attempt)) {
            setFailed(true);
            callbacks.current.onError?.(event);
          }
        }}
      />
      {failed &&
        (fallback || (fallbackSrc && <img {...props} src={fallbackSrc} alt={alt} style={style} />))}
    </>
  );
}
export default function RecoverableImage(props) {
  // A new URL gets a new controller; old URL load/error events cannot replace the new image.
  return <ImageSession key={props.src || ''} {...props} />;
}
